import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchLectureData } from '../services/googleSheets';
import DesktopLayout from '../layouts/DesktopLayout';
import MobileLayout from '../layouts/MobileLayout';
import { useConfig } from '../config/ConfigContext';
import FloatingShareButton from '../components/FloatingShareButton';
import { extractVideoId } from '../utils/youtubeUtils';

// Simple mobile detection using window width
const isMobile = () => window.innerWidth <= 768;

/**
 * Page for displaying all lectures by a specific speaker
 * @returns {JSX.Element}
 */
const SpeakerPage = () => {
  const { speakerName } = useParams();
  const { config, loading: configLoading, error: configError, theme } = useConfig();
  const [lectures, setLectures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Decode the speaker name from URL parameter
  const decodedSpeakerName = decodeURIComponent(speakerName);

  useEffect(() => {
    const loadSpeakerLectures = async () => {
      // Wait for config and speakerName
      if (configLoading || !config || !speakerName) {
        console.log('SpeakerPage: Waiting for config/speakerName...');
        return;
      }
      // Handle config error
      if (configError) {
        console.error('SpeakerPage: Config loading failed:', configError);
        setError(`Failed to load configuration: ${configError.message || configError}`);
        setLoading(false);
        return;
      }
      // Ensure google_sheet_id is available
      if (!config.google_sheet_id) {
        console.error('SpeakerPage: google_sheet_id is missing from config.');
        setError('Spreadsheet ID is missing in the configuration.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log(`SpeakerPage: Fetching data for sheet ID: ${config.google_sheet_id}, Speaker: ${decodedSpeakerName}`);
        // Fetch all lectures from Google Sheets using dynamic sheet ID
        const allLectures = await fetchLectureData(config.google_sheet_id);
        console.log('Fetched all lectures:', allLectures);

        if (!Array.isArray(allLectures) || allLectures.length === 0) {
          throw new Error('No lecture data found');
        }

        // Filter lectures for this specific speaker
        // Match case-insensitive and trim whitespace
        const speakerLectures = allLectures.filter(lecture => 
          lecture.speaker && lecture.speaker.trim().toLowerCase() === decodedSpeakerName.toLowerCase().trim()
        );

        console.log(`Filtered lectures for speaker "${decodedSpeakerName}":`, speakerLectures);

        if (speakerLectures.length === 0) {
          setError(`No lectures found for ${decodedSpeakerName}`);
        } else {
          // Sort by date (newest first) or by lectureId
          const sortedLectures = [...speakerLectures].sort((a, b) => {
            // Try to sort by date first
            if (a.date && b.date) {
              const aDate = new Date(a.date);
              const bDate = new Date(b.date);
              return bDate - aDate; // Newest first
            }
            // Fall back to lectureId
            const aId = parseInt(a.lectureId.replace(/\D/g, '')) || 0;
            const bId = parseInt(b.lectureId.replace(/\D/g, '')) || 0;
            return bId - aId; // Higher IDs (newer) first
          });
          
          setLectures(sortedLectures);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading lecture data:', err);
        setError('Failed to load lecture data. Please try again later.');
        setLoading(false);
      }
    };

    loadSpeakerLectures();
  }, [decodedSpeakerName, config, configLoading, configError, speakerName]);

  // Group lectures by series/event, sorted by lecture number within each group
  const groupedLectures = useMemo(() => {
    const groups = {};
    lectures.forEach(lecture => {
      const key = lecture.series || lecture.event || 'Uncategorized';
      if (!groups[key]) groups[key] = [];
      groups[key].push(lecture);
    });
    // Sort within each group by lectureIdInSeries (ascending)
    Object.values(groups).forEach(group => {
      group.sort((a, b) => {
        const aNum = parseInt(a.lectureIdInSeries) || 0;
        const bNum = parseInt(b.lectureIdInSeries) || 0;
        return aNum - bNum;
      });
    });
    return groups;
  }, [lectures]);

  // Determine which layout to use based on device type
  const Layout = isMobile() ? MobileLayout : DesktopLayout;

  // Handle config loading/error state before rendering
  if (configLoading) {
    return <p>Loading configuration...</p>;
  }

  if (configError) {
    return <p>Error loading configuration: {configError.message || configError}</p>;
  }

  // Render loading state
  if (loading || !theme) {
    return (
      <Layout>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '18px',
          color: theme?.colors?.primary || '#1a73e8'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '16px' }}>
              Loading lectures by {decodedSpeakerName}...
            </div>
            <div style={{
              width: '40px',
              height: '40px',
              margin: '0 auto',
              border: `4px solid ${theme?.colors?.lightGray || '#f0f0f0'}`,
              borderTopColor: theme?.colors?.primary || '#1a73e8',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Page header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{
            fontSize: '32px',
            fontWeight: 'bold',
            marginBottom: '8px',
            color: theme?.colors?.headingColor || theme?.colors?.primary || '#333'
          }}>
            {decodedSpeakerName}
          </h1>

          <p style={{ color: theme?.colors?.darkGray || '#666', marginBottom: '24px' }}>
            {lectures.length} {lectures.length === 1 ? 'lecture' : 'lectures'}
            {Object.keys(groupedLectures).length > 1 && ` across ${Object.keys(groupedLectures).length} series`}
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div style={{
            padding: '24px',
            textAlign: 'center',
            color: theme?.colors?.error || '#d32f2f',
            backgroundColor: '#FFEEEE',
            borderRadius: theme?.borderRadius?.medium || '8px',
            marginBottom: '24px'
          }}>
            <p>{error}</p>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: theme?.colors?.primary || '#1a73e8',
                color: 'white',
                border: 'none',
                padding: '10px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                marginTop: '16px'
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Lectures grouped by series */}
        {!error && Object.entries(groupedLectures).map(([seriesName, seriesLectures]) => (
          <div key={seriesName} style={{ marginBottom: '40px' }}>
            {/* Series header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: isMobile() ? '18px' : '22px',
                fontWeight: 'bold',
                color: theme?.colors?.headingColor || theme?.colors?.primary || '#1e4273',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  display: 'inline-block',
                  width: '4px',
                  height: '22px',
                  backgroundColor: theme?.colors?.primary || '#1e4273',
                  borderRadius: '2px'
                }}></span>
                {seriesName}
                <span style={{
                  fontSize: '13px',
                  fontWeight: '400',
                  color: theme?.colors?.darkGray || '#666'
                }}>
                  ({seriesLectures.length})
                </span>
              </h2>
              <Link
                to={`/events/${encodeURIComponent(seriesName)}`}
                style={{
                  color: theme?.colors?.linkColor || theme?.colors?.primary || '#1e4273',
                  textDecoration: 'none',
                  fontWeight: '500',
                  fontSize: '14px',
                  whiteSpace: 'nowrap'
                }}
              >
                View all &#x2192;
              </Link>
            </div>

            {/* Lecture cards grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile() ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: isMobile() ? '12px' : '20px'
            }}>
              {seriesLectures.map((lecture, index) => {
                const hasLectureNumber = lecture.lectureIdInSeries && lecture.lectureIdInSeries.toString().trim() !== '';
                const videoId = extractVideoId(lecture.fullVideoUrl || lecture.summaryVideoUrl);
                const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;

                const getHeaderText = () => {
                  if (hasLectureNumber) return `Lecture ${lecture.lectureIdInSeries}`;
                  if (lecture.date) {
                    try {
                      return new Date(lecture.date).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric'
                      });
                    } catch { /* fall through */ }
                  }
                  return `Lecture ${index + 1}`;
                };

                return (
                  <Link
                    key={lecture.lectureId}
                    to={`/lecture/${lecture.lectureId}`}
                    style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                  >
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
                      border: '1px solid rgba(0, 0, 0, 0.05)',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      cursor: 'pointer',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.05)';
                    }}
                    >
                      {/* Header — just lecture number/date, no series subtitle (redundant) */}
                      <div style={{
                        backgroundColor: theme?.colors?.primary || '#1e4273',
                        padding: isMobile() ? '10px' : '12px',
                        textAlign: 'center',
                        color: 'white',
                        borderRadius: '12px 12px 0 0'
                      }}>
                        <div style={{
                          fontSize: isMobile() ? '14px' : '15px',
                          fontWeight: 'bold'
                        }}>
                          {getHeaderText()}
                        </div>
                      </div>

                      {/* YouTube Thumbnail */}
                      <div style={{
                        position: 'relative',
                        paddingTop: '56.25%',
                        backgroundColor: '#f3f4f6',
                        overflow: 'hidden'
                      }}>
                        {thumbnailUrl ? (
                          <img
                            src={thumbnailUrl}
                            alt={lecture.title}
                            style={{
                              position: 'absolute',
                              top: 0, left: 0,
                              width: '100%', height: '100%',
                              objectFit: 'cover'
                            }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div style={{
                            position: 'absolute',
                            top: '50%', left: '50%',
                            transform: 'translate(-50%, -50%)'
                          }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                              <path d="M12 1C10.34 1 9 2.34 9 4V12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12V4C15 2.34 13.66 1 12 1Z" fill={theme?.colors?.primary || '#1e4273'} />
                              <path d="M17 11C17 11 17 12 17 12C17 14.76 14.76 17 12 17C9.24 17 7 14.76 7 12V11H5V12C5 15.64 7.81 18.64 11.28 18.97V22H12.72V18.97C16.19 18.64 19 15.64 19 12V11H17Z" fill={theme?.colors?.primary || '#1e4273'} />
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Info Box */}
                      <div style={{
                        backgroundColor: theme?.colors?.cardInfoBg || '#f0ece4',
                        background: `linear-gradient(to bottom, ${theme?.colors?.cardInfoBg || '#f0ece4'} 0%, ${theme?.colors?.cardInfoBgEnd || '#e8e3d9'} 100%)`,
                        padding: isMobile() ? '12px' : '16px',
                        textAlign: 'center',
                        minHeight: isMobile() ? '70px' : '90px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flex: 1
                      }}>
                        <h3 style={{
                          margin: '0 0 6px 0',
                          fontSize: isMobile() ? '13px' : '15px',
                          fontWeight: 'bold',
                          color: theme?.colors?.primary || '#1e4273',
                          lineHeight: 1.3,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {lecture.title || 'Untitled Lecture'}
                        </h3>
                        {lecture.date && (
                          <div style={{
                            fontSize: isMobile() ? '11px' : '12px',
                            color: '#6c757d',
                            marginTop: 'auto'
                          }}>
                            {new Date(lecture.date).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric'
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}

        {/* Empty state */}
        {!error && lectures.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            backgroundColor: theme?.colors?.lightGray || '#f0f0f0',
            borderRadius: theme?.borderRadius?.medium || '8px'
          }}>
            <h3 style={{ marginBottom: '16px' }}>No lectures found</h3>
            <p>No lectures are available for this speaker yet.</p>
          </div>
        )}
      </div>
      <FloatingShareButton 
        pageTitle={`${decodedSpeakerName} - Lectures`}
        pageDescription={`Browse all lectures by ${decodedSpeakerName}`}
      />
    </Layout>
  );
};

export default SpeakerPage;